import { useEffect, useState, useCallback } from 'react';
import dayjs from 'dayjs';
import axios from '../api/axios';

export function useTransfersPage(userId) {
  const [rows, setRows] = useState([]);
  const [products, setProducts] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(false);

  // filters
  const [searchText, setSearchText] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterSite, setFilterSite] = useState('');
  const [filterStartDate, setFilterStartDate] = useState(null);
  const [filterEndDate, setFilterEndDate] = useState(null);

  // pagination + sorting
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });

  const [rowCount, setRowCount] = useState(0);

  const [sortModel, setSortModel] = useState([
    { field: 'created_at', sort: 'desc' },
  ]);

  // summaries
  const [summary, setSummary] = useState([]);
  const [transferFromSummary, setTransferFromSummary] = useState([]);
  const [transferToSummary, setTransferToSummary] = useState([]);
  const [finishedProjectByDate, setFinishedProjectByDate] = useState([]);

  const transferTypeLabels = {
    supplier: '供應商來貨',
    daily_operation: '日常消耗',
    finished_project: '盤點',
    transfer: '臨時調貨',
  };

  const fetchProducts = useCallback(async () => {
    const res = await axios.get('/products');
    setProducts(res.data);
  }, []);

  const fetchSites = useCallback(async () => {
    const res = await axios.get('/sites');
    setSites(res.data.filter(s => s.site_status !== 'delivered'));
  }, []);

  const runFetchTransfers = useCallback(async (params = {}) => {
    setLoading(true);
    try {
      const res = await axios.get('/transfers', { params });

      const {
        items = [],
        total = 0,
        summary = [],
        transferFromSummary = [],
        transferToSummary = [],
        finishedProjectByDateSummary = [],
      } = res.data;

      const formatted = items.map((t) => ({
        ...t,
        id: t.id,
        created_at: t.created_at
          ? dayjs(t.created_at).format('YYYY-MM-DD HH:mm:ss')
          : '',
      }));

      setRows(formatted);
      setRowCount(total);
      setSummary(summary);
      setTransferFromSummary(transferFromSummary);
      setTransferToSummary(transferToSummary);
      setFinishedProjectByDate(finishedProjectByDateSummary);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchTransfers = useCallback(async () => {
    const params = {};

    if (searchText) params.search = searchText;
    if (filterType) params.transfer_type = filterType;
    if (filterSite) params.site_id = filterSite;

    if (filterStartDate)
      params.expected_start_date = dayjs(filterStartDate).format('YYYY-MM-DD');
    if (filterEndDate)
      params.expected_end_date = dayjs(filterEndDate).format('YYYY-MM-DD');

    params.page = paginationModel.page;
    params.page_size = paginationModel.pageSize;

    if (sortModel[0]) {
      params.sort_field = sortModel[0].field;
      params.sort_order = sortModel[0].sort;
    }

    await runFetchTransfers(params);
  }, [
    searchText,
    filterType,
    filterSite,
    filterStartDate,
    filterEndDate,
    paginationModel,
    sortModel,
    runFetchTransfers,
  ]);

  useEffect(() => {
    fetchProducts();
    fetchSites();
  }, [fetchProducts, fetchSites]);

  useEffect(() => {
    fetchTransfers();
  }, [fetchTransfers]);

  const resetFilter = () => {
    setSearchText('');
    setFilterType('');
    setFilterSite('');
    setFilterStartDate(null);
    setFilterEndDate(null);

    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    runFetchTransfers({});
  };

  return {
    rows,
    products,
    sites,
    loading,
    rowCount,
    summary,
    transferFromSummary,
    transferToSummary,
    finishedProjectByDate,
    transferTypeLabels,

    searchText, setSearchText,
    filterType, setFilterType,
    filterSite, setFilterSite,
    filterStartDate, setFilterStartDate,
    filterEndDate, setFilterEndDate,

    paginationModel, setPaginationModel,
    sortModel, setSortModel,

    fetchTransfers,
    resetFilter,
  };
}
